import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { fmtIDR } from "../../lib/api";
import { inputCls, Field, Panel, ActiveBadge, FormActions, errMsg } from "./ui";
import MonogramTile from "../MonogramTile";
import DeleteButton from "./DeleteButton";
import ProductMinOrderPanel from "./ProductMinOrderPanel";
import ProductPricePanel from "./ProductPricePanel";
import PriceHistoryPanel from "./PriceHistoryPanel";
import StockCell from "./StockCell";
import InlineEditCell from "./InlineEditCell";

const EMPTY = { game_id: "", server_id: "", category_slug: "", name: "", description: "", sell_price_idr: "", unit: "unit", active: true, min_order_idr: "" };

export default function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [games, setGames] = useState([]);
  const [servers, setServers] = useState([]);
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/products").then((r) => setProducts(r.data));
  useEffect(() => {
    load();
    Promise.all([api.get("/admin/games"), api.get("/servers"), api.get("/categories")]).then(([g, s, c]) => {
      setGames(g.data); setServers(s.data); setCats(c.data);
    });
  }, []);

  const reset = () => { setForm(EMPTY); setEditId(null); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, server_id: form.server_id || null, sell_price_idr: Number(form.sell_price_idr), min_order_idr: form.min_order_idr === "" ? null : Number(form.min_order_idr) };
    try {
      if (editId) await api.patch(`/products/${editId}`, payload);
      else await api.post("/products", payload);
      toast.success(editId ? "Produk diperbarui" : "Produk ditambahkan");
      reset(); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  const edit = (p) => {
    setEditId(p.id);
    setForm({ game_id: p.game_id, server_id: p.server_id || "", category_slug: p.category_slug, name: p.name,
      description: p.description || "", sell_price_idr: p.sell_price_idr, unit: p.unit || "unit", active: p.active, min_order_idr: p.min_order_idr || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const gameServers = servers.filter((s) => s.game_id === form.game_id);
  const nameOf = (list, id) => list.find((x) => x.id === id)?.name || "-";

  return (
    <>
      <ProductPricePanel products={products} games={games} cats={cats} onSaved={load} />
      <PriceHistoryPanel games={games} refreshKey={products} />
      <ProductMinOrderPanel products={products} games={games} cats={cats} onSaved={load} />
      <Panel title={editId ? "Ubah Produk" : "Tambah Produk"} testId="products-form-panel">
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Game">
            <select data-testid="product-game-select" required className={inputCls} value={form.game_id} onChange={(e) => setForm({ ...form, game_id: e.target.value, server_id: "" })}>
              <option value="">Pilih game</option>
              {games.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </Field>
          <Field label="Server">
            <select data-testid="product-server-select" className={inputCls} value={form.server_id} onChange={set("server_id")}>
              <option value="">Semua server / tidak spesifik</option>
              {gameServers.map((s) => <option key={s.id} value={s.id}>{s.name}{s.region ? ` · ${s.region}` : ""}{s.faction ? ` · ${s.faction}` : ""}</option>)}
            </select>
          </Field>
          <Field label="Kategori">
            <select data-testid="product-category-select" required className={inputCls} value={form.category_slug} onChange={set("category_slug")}>
              <option value="">Pilih kategori</option>
              {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Nama Produk" className="md:col-span-2"><input data-testid="product-name-input" required className={inputCls} value={form.name} onChange={set("name")} /></Field>
          <Field label="Satuan"><input data-testid="product-unit-input" required className={inputCls} value={form.unit} onChange={set("unit")} /></Field>
          <Field label="Deskripsi" className="md:col-span-2"><textarea data-testid="product-desc-input" rows={2} className={inputCls} value={form.description} onChange={set("description")} /></Field>
          <Field label="Harga Jual (IDR)"><input data-testid="product-price-input" type="number" min="1" required className={inputCls} value={form.sell_price_idr} onChange={set("sell_price_idr")} /></Field>
          <Field label="Minimal Pembelian (IDR, 0 = tanpa minimal)"><input data-testid="product-min-order-input" type="number" min="0" placeholder={!editId ? `Kosong = bawaan kategori` : "0"} className={inputCls} value={form.min_order_idr} onChange={set("min_order_idr")} /></Field>
          <label className="flex items-center gap-2 text-sm text-[#F5F3ED]">
            <input data-testid="product-active-checkbox" type="checkbox" className="accent-[#D4AF37]" checked={form.active} onChange={set("active")} /> Aktif (tampil di marketplace)
          </label>
          <div className="md:col-span-2 flex md:justify-end"><FormActions editing={!!editId} onCancel={reset} saving={saving} testPrefix="product" /></div>
        </form>
        <p className="text-xs text-[#A8A8AD] mt-4">Klik angka harga atau stok di tabel untuk mengubah. Enter atau klik di luar untuk simpan, Esc untuk batal.</p>
      </Panel>

      <Panel title="Daftar Produk" testId="products-list-panel">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-widest text-[#A8A8AD] border-b border-[#35353B]">
              <th className="py-2">Produk</th><th className="py-2">Game / Server</th><th className="py-2 text-right">Harga</th><th className="py-2 pr-6 text-right">Stok</th><th className="py-2">Status</th><th className="py-2 text-right">Aksi</th>
            </tr></thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} data-testid={`admin-product-row-${p.id}`} className="border-b border-[#35353B]">
                  <td className="py-3"><div className="flex items-center gap-3"><MonogramTile name={games.find((g) => g.id === p.game_id)?.name || p.name} className="w-12 h-12 rounded-sm shrink-0" textSize="text-[10px]" testId={`product-thumb-${p.id}`} /><div className="text-[#F5F3ED]">{p.name}<div className="text-xs text-[#A8A8AD]">{p.category_slug}</div></div></div></td>
                  <td className="py-3 text-[#A8A8AD]">{nameOf(games, p.game_id)}<div className="text-xs">{p.server_id ? nameOf(servers, p.server_id) : "Semua server"}</div></td>
                  <td data-testid={`product-price-${p.id}`} className="py-3 text-right text-[#D4AF37]"><InlineEditCell value={p.sell_price_idr} display={fmtIDR(p.sell_price_idr)}
                    minExclusive invalidMsg="Harga harus lebih dari 0" testId={`product-price-edit-${p.id}`} title="Klik untuk ubah harga, Enter untuk simpan"
                    onCommit={async (n) => { const r = await api.patch(`/products/${p.id}`, { sell_price_idr: n }); toast.success(`Harga ${p.name}: ${fmtIDR(r.data.sell_price_idr)}`); setProducts((ps) => ps.map((x) => (x.id === p.id ? r.data : x))); }} />{p.min_order_idr > 0 && <div className="text-[10px] text-[#A8A8AD]">min {fmtIDR(p.min_order_idr)}</div>}</td>
                  <td data-testid={`product-stock-${p.id}`} className="py-3 pr-6 text-right text-[#F5F3ED]"><StockCell product={p} testId={`product-stock-edit-${p.id}`}
                    onSaved={(n) => setProducts((ps) => ps.map((x) => (x.id === p.id ? { ...x, stock_available: n } : x)))} /></td>
                  <td className="py-3"><ActiveBadge active={p.active} /></td>
                  <td className="py-3 text-right"><button data-testid={`edit-product-${p.id}`} onClick={() => edit(p)} className="text-[#D4AF37] hover:underline text-xs">Ubah</button>
                    <DeleteButton endpoint={`/products/${p.id}`} name={p.name} testId={`delete-product-${p.id}`}
                      note={p.stock_available > 0 ? `Sisa stok ${p.stock_available} ikut hilang dari penjualan.` : ""} onDone={() => { if (editId === p.id) reset(); load(); }} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </>
  );
}
